import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, GitCompare, ArrowRight, Plus, X, Bookmark, Trash2, FileDown } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "@/lib/api";
import { inputCls, btnPrimary } from "@/components/Modal";
import SensoryRadar from "@/components/SensoryRadar";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import StatusBadge from "@/components/StatusBadge";
import AllergenChips from "@/components/AllergenChips";
import { inr, label } from "@/lib/constants";

const MAX_SIDES = 4;

const SENSORY_KEYS = [
  ["appearance_score", "Appearance"],
  ["aroma_score", "Aroma"],
  ["texture_score", "Texture"],
  ["taste_score", "Taste"],
  ["aftertaste_score", "Aftertaste"],
  ["overall_liking_score", "Overall liking"],
];

const NUTRIENT_KEYS = [
  ["energy_kcal", "Energy (kcal)"],
  ["protein_g", "Protein (g)"],
  ["carbohydrate_g", "Carbohydrate (g)"],
  ["total_sugars_g", "Total sugars (g)"],
  ["added_sugars_g", "Added sugars (g)"],
  ["total_fat_g", "Total fat (g)"],
  ["saturated_fat_g", "Saturated fat (g)"],
  ["sodium_mg", "Sodium (mg)"],
];

function MetricRow({ name, values, formatter = (v) => v, best = "none", testid }) {
  const numbers = values.filter((v) => typeof v === "number" && !Number.isNaN(v));
  const target = best === "low" ? Math.min(...numbers) : best === "high" ? Math.max(...numbers) : null;
  return (
    <tr className="border-b border-[#F4EFEA] last:border-0" data-testid={testid}>
      <td className="px-6 py-2.5 text-[#2A1810] whitespace-nowrap">{name}</td>
      {values.map((v, i) => {
        const isBest = target != null && numbers.length > 1 && v === target;
        return (
          <td key={i} className={`px-4 py-2.5 text-right whitespace-nowrap ${isBest ? "font-bold text-[#15803D]" : "text-[#5C4A3E]"}`}>
            {v == null ? <span className="text-[#8C7A6B]">—</span> : formatter(v)}
          </td>
        );
      })}
    </tr>
  );
}

function SavedRanges({ ids, onOpen }) {
  const { canWrite } = useAuth();
  const confirm = useConfirm();
  const [name, setName] = useState("");
  const { data: ranges = [], refetch } = useQuery({
    queryKey: ["ranges"],
    queryFn: async () => (await api.get("/ranges")).data,
  });

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/ranges", { name, note: "", formula_ids: ids });
      toast.success(`Range "${name}" saved`);
      setName("");
      refetch();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (r) => {
    const ok = await confirm({
      title: "Delete range",
      message: `Delete the proposed range "${r.name}"?`,
      confirmLabel: "Delete range",
    });
    if (!ok) return;
    try {
      await api.delete(`/ranges/${r.id}`);
      toast.success("Range deleted");
      refetch();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm" data-testid="saved-ranges-card">
      <div className="flex items-center gap-2 mb-4">
        <Bookmark size={16} className="text-[#C5A059]" />
        <h2 className="font-display text-lg font-semibold text-[#1C100B]">Proposed Ranges</h2>
      </div>

      {canWrite && ids.length >= 2 && (
        <form onSubmit={save} className="flex flex-col sm:flex-row gap-2 mb-4" data-testid="save-range-form">
          <input
            required value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Name this range, e.g. Festive Gifting 2026"
            className={`${inputCls} flex-1`} data-testid="range-name-input"
          />
          <button type="submit" className={btnPrimary} data-testid="save-range-button">
            Save this set
          </button>
        </form>
      )}

      <div className="space-y-2">
        {ranges.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-3 border border-[#F4EFEA] rounded-lg px-4 py-2.5" data-testid={`range-row-${r.id}`}>
            <button onClick={() => onOpen(r.formula_ids)} className="text-left min-w-0" data-testid={`open-range-${r.id}`}>
              <div className="text-sm font-medium text-[#2A1810] truncate hover:text-[#B08B46]">{r.name}</div>
              <div className="text-xs text-[#8C7A6B] truncate">
                {r.formulas.map((f) => f.name).join(" · ")}
              </div>
            </button>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={async () => {
                  try {
                    await downloadFile(`/ranges/${r.id}/summary.pdf`, `smoor-range-${r.id}.pdf`, {}, "application/pdf");
                  } catch (err) {
                    toast.error(apiError(err));
                  }
                }}
                className="p-2 rounded hover:bg-[#FAF6F0] text-[#5C4A3E]"
                title="Download leadership summary PDF"
                data-testid={`download-range-${r.id}`}
              >
                <FileDown size={14} />
              </button>
              {canWrite && (
                <button onClick={() => remove(r)} className="p-2 rounded hover:bg-[#FEF2F2] text-[#B91C1C]" data-testid={`delete-range-${r.id}`}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
        {!ranges.length && (
          <p className="text-sm text-[#8C7A6B]" data-testid="ranges-empty">
            No saved ranges yet. Pick a set of formulas above and save it to share the shortlist with leadership.
          </p>
        )}
      </div>
    </div>
  );
}

export default function FormulaCompare() {
  const [params, setParams] = useSearchParams();
  const [picked, setPicked] = useState(() => {
    const fromUrl = (params.get("ids") || "").split(",").filter(Boolean).slice(0, MAX_SIDES);
    return fromUrl.length ? fromUrl : ["", ""];
  });

  const selected = picked.filter(Boolean);
  const ids = [...new Set(selected)];
  const ready = ids.length >= 2;
  const duplicate = selected.length !== ids.length;

  useEffect(() => {
    if (ids.length) setParams({ ids: ids.join(",") }, { replace: true });
  }, [ids.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: formulas = [] } = useQuery({
    queryKey: ["formulas-all"],
    queryFn: async () => (await api.get("/formulas", { params: { limit: 500 } })).data,
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["compare-multi", ids.join(",")],
    queryFn: async () => (await api.get("/formulas/compare-multi", { params: { ids: ids.join(",") } })).data,
    enabled: ready,
  });

  const setAt = (index, value) => setPicked(picked.map((p, i) => (i === index ? value : p)));
  const addSlot = () => setPicked([...picked, ""]);
  const removeSlot = (index) => setPicked(picked.filter((_, i) => i !== index));

  const sides = data?.sides || [];
  const gridCols = { 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-2 xl:grid-cols-4" }[sides.length] || "md:grid-cols-2";

  return (
    <div data-testid="formula-compare-page">
      <Link to="/formulas" className="inline-flex items-center gap-2 text-sm text-[#8C7A6B] hover:text-[#2A1810] mb-4" data-testid="compare-back-link">
        <ArrowLeft size={15} /> Back to formulas
      </Link>

      <div className="mb-8 pb-4 border-b border-[#E8DFD1]">
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#1C100B]">Compare Formulas</h1>
        <p className="text-sm text-[#8C7A6B] mt-1">
          Composition, cost, allergens, nutrition and sensory for up to four formulas at once.
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-8 flex-wrap" data-testid="compare-pickers">
        <GitCompare size={18} className="text-[#C5A059] shrink-0 hidden md:block" />
        {picked.map((value, i) => (
          <div key={i} className="flex items-center gap-2">
            <select
              data-testid={`compare-select-${i}`}
              value={value}
              onChange={(e) => setAt(i, e.target.value)}
              className={`${inputCls} md:w-64`}
            >
              <option value="">Select formula {i + 1}…</option>
              {formulas.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            {picked.length > 2 && (
              <button onClick={() => removeSlot(i)} className="p-2 rounded hover:bg-[#FEF2F2] text-[#B91C1C]" data-testid={`compare-remove-${i}`}>
                <X size={14} />
              </button>
            )}
          </div>
        ))}
        {picked.length < MAX_SIDES && (
          <button
            onClick={addSlot}
            className="bg-white hover:bg-[#FAF6F0] text-[#2A1810] border border-[#E8DFD1] hover:border-[#C5A059] px-4 py-2.5 rounded-lg font-medium text-sm transition-colors flex items-center gap-2"
            data-testid="compare-add-slot"
          >
            <Plus size={14} /> Add formula
          </button>
        )}
      </div>

      {duplicate && (
        <div className="text-sm text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-4 py-3 mb-6" data-testid="compare-same-warning">
          The same formula is selected twice — duplicates are ignored.
        </div>
      )}

      {!ready ? (
        <div className="text-sm text-[#8C7A6B] bg-white border border-[#E8DFD1] rounded-xl px-6 py-12 text-center" data-testid="compare-empty">
          Choose at least two formulas above to see a side-by-side comparison.
        </div>
      ) : isLoading ? (
        <div className="text-sm text-[#8C7A6B]" data-testid="compare-loading">Comparing…</div>
      ) : isError ? (
        <div className="text-sm text-[#B91C1C]" data-testid="compare-error">{apiError(error)}</div>
      ) : data ? (
        <div className="space-y-6" data-testid="compare-results">
          <div className={`grid grid-cols-1 ${gridCols} gap-6`}>
            {sides.map((s, i) => (
              <div key={s.formula.id} className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm" data-testid={`compare-card-${i}`}>
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <h2 className="font-display text-lg font-bold text-[#1C100B]">{s.formula.name}</h2>
                  <StatusBadge value={s.formula.status} />
                  <span className="text-xs text-[#8C7A6B]">v{s.formula.version}</span>
                </div>
                <p className="text-sm text-[#5C4A3E] leading-relaxed mb-4 line-clamp-3">{s.formula.description || "No description."}</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#8C7A6B] font-semibold">BOM cost / kg</div>
                    <div className="font-bold text-[#2A1810]" data-testid={`compare-bom-${i}`}>{inr(s.bom_cost_per_kg)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#8C7A6B] font-semibold">Composition</div>
                    <div className={`font-bold ${s.formula.composition_ok ? "text-[#15803D]" : "text-[#92400E]"}`}>
                      {s.nutrition.percentage_sum}%
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#8C7A6B] font-semibold">Category</div>
                    <div className="text-[#2A1810]">{label(s.formula.category)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#8C7A6B] font-semibold">Panels recorded</div>
                    <div className="text-[#2A1810]">{s.sensory.count || 0}</div>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-[#F4EFEA]">
                  <div className="text-[10px] uppercase tracking-wider text-[#8C7A6B] font-semibold mb-1.5">Allergens</div>
                  <AllergenChips allergens={s.allergens} empty="none declared" testid={`compare-allergens-${i}`} />
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white border border-[#E8DFD1] rounded-xl shadow-sm overflow-hidden" data-testid="compare-lines-card">
            <div className="px-6 py-4 border-b border-[#F4EFEA]">
              <h2 className="font-display text-lg font-semibold text-[#1C100B]">Composition Differences</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF6F0] text-[#2A1810] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-6 py-3 text-left font-semibold">Ingredient</th>
                    {sides.map((s) => (
                      <th key={s.formula.id} className="px-4 py-3 text-right font-semibold whitespace-nowrap">{s.formula.name}</th>
                    ))}
                    <th className="px-6 py-3 text-right font-semibold">Spread</th>
                  </tr>
                </thead>
                <tbody>
                  {data.line_diff.map((d, i) => (
                    <tr key={i} className="border-b border-[#F4EFEA] last:border-0" data-testid={`compare-line-${i}`}>
                      <td className="px-6 py-2.5 font-medium text-[#2A1810] whitespace-nowrap">{d.ingredient_name}</td>
                      {d.percentages.map((p, j) => (
                        <td key={j} className="px-4 py-2.5 text-right text-[#5C4A3E]">
                          {p == null ? <span className="text-[#8C7A6B]">absent</span> : `${p}%`}
                        </td>
                      ))}
                      <td className="px-6 py-2.5 text-right font-semibold text-[#2A1810]">
                        {d.spread ? `${d.spread}%` : <span className="text-[#8C7A6B] font-normal">—</span>}
                      </td>
                    </tr>
                  ))}
                  {data.line_diff.length === 0 && (
                    <tr><td colSpan={sides.length + 2} className="px-6 py-8 text-center text-[#8C7A6B]">None of these formulas has ingredient lines yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E8DFD1] rounded-xl shadow-sm overflow-hidden" data-testid="compare-nutrition-card">
              <div className="px-6 py-4 border-b border-[#F4EFEA]">
                <h2 className="font-display text-lg font-semibold text-[#1C100B]">Nutrition per 100 g</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAF6F0] text-[#2A1810] text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-6 py-3 text-left font-semibold">Nutrient</th>
                      {sides.map((s) => (
                        <th key={s.formula.id} className="px-4 py-3 text-right font-semibold whitespace-nowrap">{s.formula.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {NUTRIENT_KEYS.map(([key, name]) => (
                      <MetricRow
                        key={key}
                        name={name}
                        values={sides.map((s) => s.nutrition.per_100g[key])}
                        best="low"
                        testid={`compare-nutrient-${key}`}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white border border-[#E8DFD1] rounded-xl shadow-sm overflow-hidden" data-testid="compare-sensory-card">
              <div className="px-6 py-4 border-b border-[#F4EFEA]">
                <h2 className="font-display text-lg font-semibold text-[#1C100B]">Average Sensory Scores</h2>
              </div>
              {sides.every((s) => !s.sensory.count) ? (
                <div className="px-6 py-8 text-center text-sm text-[#8C7A6B]" data-testid="compare-sensory-empty">
                  None of these formulas has sensory results from linked experiments yet.
                </div>
              ) : (
                <>
                  <div className="px-4 pt-4">
                    <SensoryRadar
                      series={sides.map((s) => ({ name: s.formula.name, scores: s.sensory }))}
                      height={300}
                      testid="compare-sensory-radar"
                    />
                  </div>
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#FAF6F0] text-[#2A1810] text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-6 py-3 text-left font-semibold">Attribute</th>
                        {sides.map((s) => (
                          <th key={s.formula.id} className="px-4 py-3 text-right font-semibold whitespace-nowrap">{s.formula.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {SENSORY_KEYS.map(([key, name]) => (
                        <MetricRow
                          key={key}
                          name={name}
                          values={sides.map((s) => s.sensory[key] ?? null)}
                          best="high"
                          testid={`compare-sensory-${key}`}
                        />
                      ))}
                    </tbody>
                  </table>
                  </div>
                </>
              )}
            </div>
          </div>

          <SavedRanges ids={ids} onOpen={(next) => setPicked(next)} />

          <div className="flex gap-4 flex-wrap">
            {sides.map((s, i) => (
              <Link
                key={s.formula.id}
                to={`/formulas/${s.formula.id}`}
                className="text-xs font-semibold text-[#B08B46] hover:underline inline-flex items-center gap-1"
                data-testid={`compare-open-${i}`}
              >
                Open {s.formula.name} <ArrowRight size={12} />
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
